/**
 * Client-side image prep + progress-reporting upload for every admin upload.
 * A phone photo is 3-12MB HEIC/JPEG at 4000px — far bigger than anything we
 * display — so it is decoded, downscaled and re-encoded in the browser before
 * it is ever sent. That keeps uploads small and fast, and turns an iPhone HEIC
 * into a PNG/JPEG the server can always read.
 */

const PASSTHROUGH = /(svg|gif)/i;

/**
 * Decide transparency from the PIXELS, never from `file.type` — the type lies.
 * The iPad Photos picker hands Safari a transcoded `image/jpeg` (already
 * flattened), and some pickers report an empty type for a real PNG. Sampling a
 * grid is enough: one sub-opaque pixel means the image has alpha to keep.
 */
const readAlpha = (canvas, ctx) => {
    try {
        const { width: w, height: h } = canvas;
        const step = Math.max(1, Math.floor(Math.min(w, h) / 64));
        const d = ctx.getImageData(0, 0, w, h).data;
        let transparent = false;
        for (let y = 0; y < h && !transparent; y += step) {
            for (let x = 0; x < w; x += step) {
                if (d[(y * w + x) * 4 + 3] < 250) { transparent = true; break; }
            }
        }
        const at = (x, y) => { const i = (y * w + x) * 4; return [d[i], d[i + 1], d[i + 2], d[i + 3]]; };
        const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
        const [r, g, b, a] = corners[0];
        // A logo flattened onto a solid card has four identical opaque corners
        // of the same near-white / near-black fill. A photograph does not.
        const solidCorners = a === 255
            && corners.every(([cr, cg, cb]) => Math.abs(cr - r) < 8 && Math.abs(cg - g) < 8 && Math.abs(cb - b) < 8)
            && (Math.min(r, g, b) > 235 || Math.max(r, g, b) < 24);
        return { transparent, flattened: !transparent && solidCorners };
    } catch (_) {
        return { transparent: false, flattened: false };   // tainted canvas — assume opaque
    }
};

const loadBitmap = async (file) => {
    if (typeof createImageBitmap === 'function') {
        try { return await createImageBitmap(file); } catch (_) { /* fall through */ }
    }
    const url = URL.createObjectURL(file);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }
};

export const prepareImageForUpload = async (file, { maxEdge = 1600, onFlattened } = {}) => {
    if (!file) return file;
    if (PASSTHROUGH.test(file.type || '')) return file;
    const alreadyWebSafe = /^image\/(png|jpeg|webp)$/i.test(file.type || '');
    try {
        const bmp = await loadBitmap(file);
        const w0 = bmp.width || bmp.naturalWidth;
        const h0 = bmp.height || bmp.naturalHeight;
        if (!w0 || !h0) return file;
        const scale = Math.min(1, maxEdge / Math.max(w0, h0));

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w0 * scale));
        canvas.height = Math.max(1, Math.round(h0 * scale));
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);

        const { transparent: keepAlpha, flattened } = readAlpha(canvas, ctx);
        // Tell the merchant BEFORE they wonder why their logo has a white box:
        // the transparency was gone before the browser ever handed us the file.
        if (flattened && onFlattened) onFlattened();
        if (scale === 1 && alreadyWebSafe && file.size <= 2 * 1024 * 1024) return file;
        const mime = keepAlpha ? 'image/png' : 'image/jpeg';
        const blob = await new Promise((res) => canvas.toBlob(res, mime, 0.92));
        if (!blob) return file;
        if (alreadyWebSafe && blob.size >= file.size && scale === 1) return file;
        const name = (file.name || 'image').replace(/\.[^.]+$/, '') + (keepAlpha ? '.png' : '.jpg');
        return new File([blob], name, { type: mime, lastModified: Date.now() });
    } catch (_) {
        return file;
    }
};

/** A HEIC photo reports an empty or non-image type in some pickers. */
export const looksLikeImage = (file) => {
    if (!file) return false;
    const type = (file.type || '').toLowerCase();
    if (type.startsWith('image/')) return true;
    return /\.(png|jpe?g|webp|gif|heic|heif|avif|bmp|tiff?)$/i.test(file.name || '');
};

/**
 * POST a file with REAL upload progress. `fetch` cannot report progress, so
 * this uses XHR. Resolves { ok, status, data, detail } — it never throws, so
 * every caller can show one reason.
 */
export const UPLOAD_TIMEOUT_MS = 60000;

export const uploadWithProgress = ({ url, token, file, fileField = 'image', fields = {}, onProgress, timeoutMs = UPLOAD_TIMEOUT_MS }) =>
    new Promise((resolve) => {
        const fd = new FormData();
        Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, v); });
        fd.append(fileField, file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        // Without this the UI spins forever at 99% when the server accepts the
        // body and then never answers.
        xhr.timeout = timeoutMs;
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        };
        xhr.onload = () => {
            let body = null;
            try { body = JSON.parse(xhr.responseText); } catch (_) { /* no body */ }
            const ok = xhr.status >= 200 && xhr.status < 300;
            if (ok && onProgress) onProgress(100);
            resolve({
                ok,
                status: xhr.status,
                data: ok ? (body || {}) : null,
                detail: typeof body?.detail === 'string' ? body.detail : null,
            });
        };
        xhr.onerror = () => resolve({ ok: false, status: 0, data: null, detail: null });
        xhr.ontimeout = () => resolve({ ok: false, status: 0, data: null, detail: null, timedOut: true });
        xhr.send(fd);
    });

/** Turn a failed upload result into a reason a human can act on. */
export const uploadFailureMessage = (result, t) => {
    const status = result?.status ?? 0;
    if (result?.timedOut) return t('uploads.errorTimeout');
    if (status === 401) return t('uploads.errorSession');
    if (status === 403) return t('uploads.errorPermission');
    if (status === 413) return t('uploads.errorTooLarge');
    if (status >= 500) return t('uploads.errorServer');
    if (status === 0) return t('uploads.errorNetwork');
    return result?.detail || t('uploads.errorRejected');
};
